import { type PrinterDetails } from '@goshippo/print';

import type LabelFormat from '~/types/LabelFormat';

import { LABEL_FORMATS_SIZES } from '../constants/labelFormatSizes.const';
import { EMPTY_PREFERENCES } from '../constants/preferences.const';
import { type PrintPreferences } from '../types/preferences.type';
import {
  PrintAssetType,
  PrinterConnection,
  type RemotePrintPreferences,
} from '../types/print.type';
import { fromWorkstationKey } from './workstationKey.util';

const CUPS_NETWORK_URI_SCHEMES = new Set([
  'dnssd',
  'http',
  'https',
  'ipp',
  'ipps',
  'lpd',
  'smb',
  'socket',
]);

const WINDOWS_USB_PORT = /^USB\d+$/i;
const WINDOWS_NETWORK_PORT = /^(IP_|WSD-)/i;

export const parseConnection = (
  connection: PrinterDetails['connection'],
): PrinterConnection | null => {
  if (connection === undefined || connection === null) return PrinterConnection.Unknown;

  const trimmed = connection.trim();
  if (trimmed === '') return PrinterConnection.Unknown;

  const schemeEnd = trimmed.indexOf(':');
  if (schemeEnd < 0) {
    const port = trimmed;
    if (WINDOWS_USB_PORT.test(port)) return PrinterConnection.Usb;
    if (WINDOWS_NETWORK_PORT.test(port)) return PrinterConnection.Network;
    return PrinterConnection.Unknown;
  }

  const scheme = trimmed.slice(0, schemeEnd).toLowerCase();

  if (scheme === 'usb') return PrinterConnection.Usb;
  if (scheme === 'btspp' || scheme === 'bluetooth') return PrinterConnection.Bluetooth;
  if (CUPS_NETWORK_URI_SCHEMES.has(scheme)) return PrinterConnection.Network;

  return PrinterConnection.Unknown;
};

export const parseSupportedSizes = (sizes: PrinterDetails['sizes']): string[] => {
  if (!sizes) return [];

  const supportedSizes = new Set<string>();

  sizes.forEach((size) => {
    const truncatedWidth = Math.trunc(size.in.width * 100) / 100;
    const truncatedHeight = Math.trunc(size.in.height * 100) / 100;
    supportedSizes.add(`${truncatedWidth}x${truncatedHeight}`);
  });

  return Array.from(supportedSizes);
};

export const isSizeSupported = (
  labelFormat: LabelFormat,
  printerSupportedSizes: string[],
): boolean => {
  const formatSize = LABEL_FORMATS_SIZES[labelFormat];

  if (formatSize === undefined) return true;

  // We only validate if the driver reports supported sizes.
  return printerSupportedSizes.length === 0 || printerSupportedSizes.includes(formatSize);
};

export const findDefaultPrinterFromDetails = (
  printers: readonly PrinterDetails[],
): null | string => {
  const match = printers.find((printer) => printer.default === true);
  return match?.name ?? null;
};

/** Last-resort default when QZ reports no OS default and no `default: true` flag. */
export const pickFallbackPrinterFromDetails = (
  printers: readonly PrinterDetails[],
): null | string => printers[0]?.name ?? null;

/** Resolve the best local default printer name from QZ metadata sources. */
export const resolveDefaultPrinterName = (
  getDefaultResult: null | string,
  localPrinters: readonly PrinterDetails[],
): null | string => {
  if (getDefaultResult) return getDefaultResult;

  const fromDetails = findDefaultPrinterFromDetails(localPrinters);
  if (fromDetails) return fromDetails;

  return pickFallbackPrinterFromDetails(localPrinters);
};

const getPrinter = (
  assetType: PrintAssetType,
  remotePrefs: RemotePrintPreferences,
  defaultPrinterFromOS: null | string,
): string => {
  const printerKey = remotePrefs[assetType]?.printer_key;

  if (printerKey) return fromWorkstationKey(printerKey);
  if (defaultPrinterFromOS) return defaultPrinterFromOS;

  return EMPTY_PREFERENCES[assetType].printerName;
};

export const conciliatePreferences = (
  defaultPrinterFromOS: null | string,
  remotePrefs: RemotePrintPreferences,
): PrintPreferences => {
  const { MANIFEST, PICK_LIST, SHIPPING_LABEL } = remotePrefs;

  return {
    [PrintAssetType.Manifest]: {
      color: MANIFEST?.print_color ?? EMPTY_PREFERENCES.MANIFEST.color,
      initiation: MANIFEST?.print_initiation ?? EMPTY_PREFERENCES.MANIFEST.initiation,
      printerName: getPrinter(PrintAssetType.Manifest, remotePrefs, defaultPrinterFromOS),
    },
    [PrintAssetType.PickList]: {
      color: PICK_LIST?.print_color ?? EMPTY_PREFERENCES.PICK_LIST.color,
      initiation: PICK_LIST?.print_initiation ?? EMPTY_PREFERENCES.PICK_LIST.initiation,
      printerName: getPrinter(PrintAssetType.PickList, remotePrefs, defaultPrinterFromOS),
    },
    [PrintAssetType.ShippingLabel]: {
      color: SHIPPING_LABEL?.print_color ?? EMPTY_PREFERENCES.SHIPPING_LABEL.color,
      initiation: SHIPPING_LABEL?.print_initiation ?? EMPTY_PREFERENCES.SHIPPING_LABEL.initiation,
      printerName: getPrinter(PrintAssetType.ShippingLabel, remotePrefs, defaultPrinterFromOS),
    },
  };
};
